import { useEffect, useMemo, useRef } from 'react';
import { useAuthContext } from '../context/AuthContext';
import { useIsIncarnated } from './useAuthState';

/**
 * Refetch the given RTK Query hook when the effective auth identity changes
 * (user id, session id, incarnation state).
 *
 * Usage:
 *   const { data, refetch } = useSomeQuery(args, options);
 *   useRefetchOnAuthChange(refetch);
 */
export function useRefetchOnAuthChange(refetch, options = {}) {
  const { enabled = true } = options;
  const { data } = useAuthContext();
  const isIncarnated = useIsIncarnated();

  const authKey = useMemo(() => {
    const userId = data?.user?.id || 'guest';
    const sessionId = data?.sessionId || '';
    const inc = isIncarnated ? 1 : 0;
    return `${userId}:${sessionId}:${inc}`;
  }, [data?.user?.id, data?.sessionId, isIncarnated]);

  const prevKey = useRef(authKey);

  useEffect(() => {
    if (!refetch) return;
    if (!enabled) return;
    if (prevKey.current !== authKey) {
      prevKey.current = authKey;
      try {
        refetch();
      } catch (e) {
        // Swallow refetch errors when query hasn't started yet
      }
    }
  }, [authKey, refetch, enabled]);
}
